import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { FOCUS_RING } from './constants';

const RECENT_KEY = 'azamein.recentFonts';
const SAMPLE_HE = 'שלום זכר · מזל טוב';
const SAMPLE_EN = 'Mazel Tov · Simcha';
const SAMPLE_DECO = '❦ ❧ ✿ ❀ ✦ ★ ✧ ❖';

const readRecent = () => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(v) ? v.slice(0, 5) : [];
  } catch (e) {
    return [];
  }
};

const groupOf = (f) => (f.decorative ? 'Decorative' : f.uploaded ? 'Your fonts' : f.hebrew ? 'Hebrew & Yiddish' : 'English');
const GROUP_ORDER = ['Hebrew & Yiddish', 'Your fonts', 'English', 'Decorative'];

/**
 * Font picker with a live preview of every font (InDesign / Canva style):
 * search, recently used, groups, and a sample line drawn in each font.
 */
export const FontPicker = ({ value, fonts, onChange, sampleText }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState(readRecent);
  const searchRef = useRef(null);

  const list = useMemo(
    () => (fonts.some((f) => f.family === value) || !value ? fonts : [{ family: value, label: value, uploaded: true }, ...fonts]),
    [fonts, value]
  );
  const byFamily = useMemo(() => Object.fromEntries(list.map((f) => [f.family, f])), [list]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => searchRef.current?.focus(), 30);
    }
  }, [open]);

  const pick = (family) => {
    onChange(family);
    const next = [family, ...recent.filter((r) => r !== family)].slice(0, 5);
    setRecent(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (e) {
      // ignore
    }
    setOpen(false);
  };

  const q = query.trim().toLowerCase();
  const visible = list.filter((f) => !q || (f.label || f.family).toLowerCase().includes(q));
  const groups = GROUP_ORDER.map((g) => [g, visible.filter((f) => groupOf(f) === g)]).filter(([, items]) => items.length);
  const recentFonts = q ? [] : recent.map((r) => byFamily[r]).filter(Boolean);
  const custom = (sampleText || '').trim().split('\n')[0].slice(0, 40);

  const Row = (f) => {
    const on = f.family === value;
    const sample = f.decorative ? SAMPLE_DECO : custom || (f.hebrew || f.uploaded ? SAMPLE_HE : SAMPLE_EN);
    return (
      <button
        key={f.family}
        type="button"
        onClick={() => pick(f.family)}
        className={cn(
          'w-full text-left px-3 py-2 rounded-xl flex items-center gap-3 hover:bg-cream',
          on && 'bg-cream',
          FOCUS_RING
        )}
        aria-pressed={on}
        data-testid={`font-option-${f.family}`}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-bold text-[#6B665C] truncate">{f.label || f.family}</span>
          <span dir="auto" className="block text-[22px] leading-tight text-ink truncate" style={{ fontFamily: `'${f.family}'` }}>
            {sample}
          </span>
        </span>
        {on ? <Check className="h-4 w-4 shrink-0 text-ink" aria-hidden="true" /> : null}
      </button>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Font: ${byFamily[value]?.label || value}`}
          className={cn(
            'h-11 w-full rounded-xl bg-white border border-[#DCD6C8] px-3 flex items-center justify-between gap-2 text-[17px] text-ink',
            FOCUS_RING
          )}
          data-testid="font-select"
        >
          <span className="truncate" style={{ fontFamily: `'${value}'` }}>{byFamily[value]?.label || value || 'Choose a font'}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-[#6B665C]" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-2 rounded-2xl bg-white" data-testid="font-picker">
        <label className="flex items-center gap-2 h-10 px-3 rounded-xl border border-[#DCD6C8] mb-2">
          <Search className="h-4 w-4 text-[#6B665C]" aria-hidden="true" />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fonts"
            aria-label="Search fonts"
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </label>
        <div className="max-h-[380px] overflow-y-auto pr-1 flex flex-col gap-1">
          {recentFonts.length ? (
            <>
              <p className="px-3 pt-1 text-[11px] font-extrabold uppercase tracking-wide text-[#6B665C]">Recently used</p>
              {recentFonts.map(Row)}
            </>
          ) : null}
          {groups.map(([g, items]) => (
            <div key={g} className="flex flex-col gap-1">
              <p className="px-3 pt-2 text-[11px] font-extrabold uppercase tracking-wide text-[#6B665C]">{g}</p>
              {items.map(Row)}
            </div>
          ))}
          {!groups.length ? <p className="px-3 py-6 text-sm text-center text-[#6B665C]">No font matches “{query}”.</p> : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FontPicker;
